import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaChevronRight,
  FaChevronLeft,
  FaWallet,
  FaHistory,
  FaUniversity,
  FaCheckCircle,
  FaClock,
  FaHiking,
  FaUser,
  FaSearch,
  FaPlusCircle,
  FaCog
} from "react-icons/fa";
import axios from "axios";
import {
  getGuideEligibleAmount,
  getGuideWithdrawalStatus,
  saveGuideBankDetails,
  requestGuideWithdrawal,
} from "../services/guideWithdrawalService";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./Register.css"; // Reuse premium theme
import "./HotelBookingList.css"; // Reuse dashboard styles
import NavUser from "../components/NavUser";
import { getApiBaseUrl } from "../utils/apiConfig";

function TourBookingList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [withdrawalSummary, setWithdrawalSummary] = useState({
    eligibleAmount: 0,
    lastAmount: 0,
    status: "Not Available",
    requestedAt: null,
    bankDetailsExist: false,
    history: [],
  });
  const [showBankForm, setShowBankForm] = useState(false);
  const [bankForm, setBankForm] = useState({
    account_holder_name: "",
    bank_name: "",
    bank_branch: "",
    account_number: "",
  });

  const sliderRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("jwt_token");
  const userId = user?.id;
  const API_URL = getApiBaseUrl();

  useEffect(() => {
    if (!userId) return;

    const fetchBookings = async () => {
      try {
        const res = await axios.get(`${API_URL}/guide/${userId}/bookings`);
        setBookings(res.data.bookings || []);
      } catch (err) {
        console.error("Error fetching bookings:", err);
      }
    };

    const loadWithdrawalData = async () => {
      try {
        const eligible = await getGuideEligibleAmount(token);
        const status = await getGuideWithdrawalStatus(token);

        setWithdrawalSummary({
          eligibleAmount: eligible.total || 0,
          lastAmount: status.amount || 0,
          status: status.status || "Not Available",
          requestedAt: status.requestedAt || null,
          bankDetailsExist: false,
          history: [], // Logic for history if needed
        });
      } catch (err) {
        console.error("Error fetching withdrawal:", err);
      }
    };

    fetchBookings();
    loadWithdrawalData();
  }, [userId, token, API_URL]);

  const formatDate = (date) => new Date(date).toISOString().split("T")[0];
  const today = formatDate(new Date());

  const getFilteredByTab = () => {
    if (activeTab === "today") return bookings.filter((b) => formatDate(b.booking_date) === today);
    if (activeTab === "upcoming") return bookings.filter((b) => formatDate(b.booking_date) >= today);
    if (activeTab === "past") return bookings.filter((b) => formatDate(b.booking_date) < today);
    return bookings;
  };

  const filteredBookings = getFilteredByTab().filter((b) =>
    b.tour_listing?.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleBankInputChange = (e) => {
    const { name, value } = e.target;
    setBankForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveBankDetails = async (e) => {
    e.preventDefault();
    try {
      const res = await saveGuideBankDetails(bankForm, token);
      toast.success(res.message || "Bank details saved");
      setWithdrawalSummary(p => ({ ...p, bankDetailsExist: true }));
      setShowBankForm(false);
    } catch (err) {
      toast.error("Failed to save bank details");
    }
  };

  const handleWithdrawalRequest = async () => {
    try {
      const res = await requestGuideWithdrawal(token);
      toast.success(res.message);
    } catch (e) {
      toast.error(e.response?.data?.message || "Withdrawal request failed");
    }
  };

  return (
    <div className="agent-page-premium">
      <NavUser />
      <ToastContainer theme="colored" />
      
      <div className="premium-hero-mini">
        <div className="hero-content">
          <h1>Guide Dashboard</h1>
          <p>Manage your tours, track bookings, and handle your earnings.</p>
        </div>
      </div>

      <div className="premium-container-wide">
        <div className="dashboard-grid-main">
          {/* Financial Overview */}
          <div className="dashboard-column">
            <div className="dashboard-section-header">
              <h2><FaWallet /> Earnings Overview</h2>
            </div>
            
            <div className="premium-glass-card financial-card">
              <div className="withdraw-status-bar">
                <span className="status-label">Current Status:</span>
                <span className={`status-val ${withdrawalSummary.status.toLowerCase().replace(' ', '-')}`}>{withdrawalSummary.status}</span>
              </div>

              <div className="earnings-grid">
                <div className="earning-item">
                  <span className="label">Available for Withdrawal</span>
                  <span className="value">Rs. {withdrawalSummary.eligibleAmount.toLocaleString()}</span>
                </div>
                <div className="earning-item">
                  <span className="label">Last Payout</span>
                  <span className="value">Rs. {withdrawalSummary.lastAmount.toLocaleString()}</span>
                  <span className="sub-val">{withdrawalSummary.requestedAt ? new Date(withdrawalSummary.requestedAt).toLocaleDateString() : "No history"}</span>
                </div>
              </div>

              <div className="withdraw-actions">
                <button className="btn-v2-secondary" onClick={() => setShowBankForm(true)}>
                  <FaUniversity /> {withdrawalSummary.bankDetailsExist ? "Edit Bank Info" : "Setup Bank"}
                </button>
                <button className="btn-v2-primary" onClick={handleWithdrawalRequest}>
                  Request Payout
                </button>
              </div>
            </div>

            <div className="dashboard-section-header" style={{ marginTop: '30px' }}>
              <h2><FaPlusCircle /> Quick Actions</h2>
            </div>
            <div className="premium-glass-card history-card" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <button className="btn-minimal" onClick={() => navigate("/addtour")}>Create New Tour</button>
              <button className="btn-minimal" onClick={() => navigate("/guide-profile")}>Manage Profile</button>
            </div>
          </div>

          {/* Bookings Overview */}
          <div className="dashboard-column">
            <div className="dashboard-section-header">
              <h2><FaHiking /> Your Tour Reservations</h2>
              <div className="filter-pills">
                {["all", "today", "upcoming", "past"].map((tab) => (
                  <button 
                    key={tab} 
                    className={`pill-btn ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <div className="search-bar-v2">
              <FaSearch />
              <input 
                type="text" 
                placeholder="Search by tour name or traveler..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="booking-v2-list">
              {filteredBookings.length === 0 ? (
                <div className="premium-glass-card empty-state">
                  <FaCalendarAlt />
                  <p>No tour bookings found</p>
                </div>
              ) : (
                filteredBookings.map((b) => (
                  <div key={b.id} className="premium-booking-card-v2 hotel-card">
                    <div className="card-top">
                      <div className="hotel-info">
                        <h4 className="hotel-name">{b.tour_listing?.title}</h4>
                        <div className="guest-info">
                          <FaUser /> <span>{b.traveler?.full_name}</span>
                        </div>
                      </div>
                      <div className={`payment-badge ${b.payment_status_id === 1 ? 'paid' : b.paidAmount > 0 ? 'partial' : 'pending'}`}>
                        {b.payment_status_id === 1 ? 'PAID' : b.paidAmount > 0 ? 'PARTIAL' : 'PENDING'}
                      </div>
                    </div>

                    <div className="booking-metrics">
                      <div className="metric">
                        <span className="m-label">Tour Date</span>
                        <span className="m-val">{new Date(b.booking_date).toLocaleDateString()}</span>
                      </div>
                      <div className="metric">
                        <span className="m-label">Paid Amount</span>
                        <span className="m-val" style={{ color: '#38a169', fontWeight: 700 }}>Rs. {(b.paidAmount || 0).toLocaleString()}</span>
                      </div>
                      <div className="metric">
                        <span className="m-label">Remaining</span>
                        <span className="m-val" style={{ color: '#e53e3e', fontWeight: 700 }}>Rs. {(b.price - (b.paidAmount || 0)).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bank Modal */}
      {showBankForm && (
        <div className="premium-modal-overlay">
          <div className="premium-glass-card modal-card">
            <div className="modal-header">
              <h3>Guide Bank Details</h3>
              <button className="close-btn" onClick={() => setShowBankForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleSaveBankDetails} className="premium-form-layout">
              <div className="premium-field">
                <label>Account Holder Name</label>
                <input name="account_holder_name" value={bankForm.account_holder_name} onChange={handleBankInputChange} required />
              </div>
              <div className="premium-field">
                <label>Select Bank</label>
                <select name="bank_name" value={bankForm.bank_name} onChange={handleBankInputChange} required>
                  <option value="">Choose Bank</option>
                  <option value="BOC">Bank of Ceylon</option>
                  <option value="Commercial">Commercial Bank</option>
                  <option value="Sampath">Sampath Bank</option>
                  <option value="HNB">Hatton National Bank</option>
                </select>
              </div>
              <div className="form-grid-2">
                <div className="premium-field">
                  <label>Branch</label>
                  <input name="bank_branch" value={bankForm.bank_branch} onChange={handleBankInputChange} required />
                </div>
                <div className="premium-field">
                  <label>Account Number</label>
                  <input name="account_number" value={bankForm.account_number} onChange={handleBankInputChange} required />
                </div>
              </div>
              <button type="submit" className="btn-premium-action">Update Bank Details</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TourBookingList;
